package wat.ui

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import org.w3c.dom.*
import wat.i18n.I18n
import wat.i18n.I18n.t
import wat.model.*
import wat.service.DataService
import wat.Supa

object SettingsTab {

    private var editMode = false
    private var editHeight = ""
    private var editGoalWeight = ""
    private var editBirthdate = ""
    private var editSex = ""
    private var enabledSports = mutableMapOf<String, Boolean>()
    private var deleteConfirmVisible = false
    private var subTab = try { kotlinx.browser.window.localStorage.getItem("wat_settingsTab") ?: "profil" } catch (_: Exception) { "profil" }

    fun jumpToActivities() { subTab = "aktivity" }

    fun render(container: HTMLDivElement, userId: String, onLogout: () -> Unit) {
        val profile = DataService.profile

        // Initialize edit fields from profile
        if (!editMode) {
            editHeight = profile.height.toString()
            editGoalWeight = if (profile.goalWeight > 0) fmt1(profile.goalWeight) else ""
            editBirthdate = profile.birthdate
            editSex = profile.sex
        }

        // Initialize sport toggle states
        if (enabledSports.isEmpty()) {
            DataService.sports.forEach { sp ->
                val def = DataService.sportDefs[sp.k]
                enabledSports[sp.k] = def?.on ?: sp.isDefault
            }
        }

        // ── Sub-navigation tabs ──
        container.appendChild(div {
            style.cssText = "display:flex;gap:6px;margin-bottom:20px"

            data class SubTab(val key: String, val label: String, val icon: String)
            listOf(
                SubTab("profil", t("settings_profile"), "\uD83D\uDC64"),
                SubTab("aktivity", t("settings_activities"), "\u26A1"),
                SubTab("system", t("settings_system"), "\u2699\uFE0F")
            ).forEach { tab ->
                val isActive = subTab == tab.key
                val activeBorder = if (isActive) "1.5px solid ${Styles.A1}60" else "1.5px solid ${Styles.CARD_BORDER}"
                val activeBg = if (isActive) "${Styles.A1}15" else Styles.CARD_BG
                val activeColor = if (isActive) Styles.A1 else Styles.TEXT_SEC
                appendChild(button("") {
                    style.cssText = "flex:1;padding:8px 8px;border-radius:12px;border:$activeBorder;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px;background:$activeBg"
                    appendChild(span(tab.icon) {
                        style.cssText = "font-size:16px;opacity:${if (isActive) "1" else "0.5"}"
                    })
                    appendChild(span(tab.label) {
                        style.cssText = "font-size:12px;font-weight:800;color:$activeColor;letter-spacing:0.5px"
                    })
                    onclick = {
                        subTab = tab.key
                        try { kotlinx.browser.window.localStorage.setItem("wat_settingsTab", tab.key) } catch (_: Exception) {}
                        MainApp.renderPage()
                        Unit
                    }
                })
            }
        })

        // ── Render content based on sub-tab ──
        when (subTab) {
            "profil" -> {
                container.appendChild(renderProfileCard(profile, userId))
                container.appendChild(renderThemeSelector())
                container.appendChild(renderTimezoneInfo())
                container.appendChild(renderLanguageSelector(profile, userId))
                container.appendChild(renderDefaultPageSelector(profile, userId))
            }
            "aktivity" -> {
                container.appendChild(renderActivityManagement())
            }
            "system" -> {
                container.appendChild(renderDbStatus())

                // Version Info
                container.appendChild(div {
                    style.cssText = "text-align:center;margin:24px 0"
                    appendChild(div {
                        style.cssText = "font-size:11px;color:${Styles.TEXT_20};font-weight:600;letter-spacing:2px"
                        textContent = "Kotlin/JS verzia 1.0.0"
                    })
                    appendChild(div {
                        style.cssText = "font-size:10px;color:${Styles.TEXT_15};margin-top:4px"
                        textContent = "Kotlin/JS + Supabase"
                    })
                })

                // Logout
                container.appendChild(div {
                    style.cssText = "padding:0;margin-bottom:12px"
                    appendChild(button("") {
                        style.cssText = "width:100%;padding:14px 16px;border-radius:14px;border:1.5px solid #fb718540;background:#fb718512;color:#fb7185;font-size:14px;font-weight:700;cursor:pointer;letter-spacing:0.5px;display:flex;align-items:center;justify-content:center;gap:10px;font-family:inherit"
                        innerHTML = "<svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='#fb7185' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4'/><polyline points='16 17 21 12 16 7'/><line x1='21' y1='12' x2='9' y2='12'/></svg><span>${t("settings_logout")}</span>"
                        onclick = { onLogout() }
                    })
                })

                // Delete Account
                container.appendChild(div {
                    style.cssText = "padding:0;margin-bottom:100px"
                    if (!deleteConfirmVisible) {
                        appendChild(button("") {
                            style.cssText = "width:100%;padding:14px 16px;border-radius:14px;border:1.5px solid #f9731640;background:#f9731612;color:#f97316;font-size:14px;font-weight:700;cursor:pointer;letter-spacing:0.5px;display:flex;align-items:center;justify-content:center;gap:10px;font-family:inherit"
                            innerHTML = "<svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='#f97316' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z'/><line x1='12' y1='9' x2='12' y2='13'/><line x1='12' y1='17' x2='12.01' y2='17'/></svg><span>${t("delete_account")}</span>"
                            onclick = {
                                deleteConfirmVisible = true
                                reRender(container, userId, onLogout)
                                Unit
                            }
                        })
                    } else {
                        val confirmWord = t("delete_confirm_word")
                        appendChild(div {
                            style.cssText = "background:#ef444415;border:2px solid #ef444450;border-radius:14px;padding:20px"

                            // Title
                            appendChild(div {
                                style.cssText = "font-size:16px;font-weight:800;color:#ef4444;margin-bottom:6px"
                                textContent = t("delete_confirm_title")
                            })
                            // Warning
                            appendChild(div {
                                style.cssText = "font-size:13px;color:${Styles.TEXT_60};margin-bottom:16px"
                                textContent = t("delete_warning")
                            })
                            // Hint
                            appendChild(div {
                                style.cssText = "font-size:12px;color:${Styles.TEXT_45};margin-bottom:8px"
                                innerHTML = "${t("delete_confirm_hint")}: <span style='color:#ef4444;font-weight:800'>$confirmWord</span>"
                            })
                            // Input
                            val deleteInput = input("text") {
                                style.cssText = "width:100%;padding:12px 14px;border-radius:10px;border:2px solid #ef444460;background:#ef444410;color:${Styles.TEXT};font-size:16px;font-weight:700;text-align:center;outline:none;box-sizing:border-box;font-family:inherit;letter-spacing:2px"
                                placeholder = confirmWord
                            }
                            appendChild(deleteInput)

                            // Create delete button first (needed for event wiring)
                            val deleteBtn = button(t("delete_account")) {
                                id = "delete-confirm-btn"
                                style.cssText = "flex:1;padding:12px;border-radius:12px;border:none;background:#ef444430;color:#ef444460;font-size:14px;font-weight:700;cursor:default;font-family:inherit"
                            }

                            // Buttons row
                            appendChild(div {
                                style.cssText = "display:flex;gap:8px;margin-top:14px"
                                appendChild(button(t("btn_cancel")) {
                                    style.cssText = "flex:1;padding:12px;border-radius:12px;border:1.5px solid ${Styles.CARD_BORDER};background:${Styles.CARD_BG};color:${Styles.TEXT_SEC};font-size:14px;font-weight:600;cursor:pointer;font-family:inherit"
                                    onclick = {
                                        deleteConfirmVisible = false
                                        reRender(container, userId, onLogout)
                                        Unit
                                    }
                                })
                                appendChild(deleteBtn)
                            })

                            // Wire up: enable button when correct word typed, delete on click
                            val cw = confirmWord.uppercase()
                            deleteInput.addEventListener("input", {
                                val match = deleteInput.value.trim().uppercase() == cw
                                deleteBtn.style.background = if (match) "#ef4444" else "#ef444430"
                                deleteBtn.style.color = if (match) "#fff" else "#ef444460"
                                deleteBtn.style.cursor = if (match) "pointer" else "default"
                                Unit
                            })
                            deleteBtn.addEventListener("click", {
                                val match = deleteInput.value.trim().uppercase() == cw
                                if (!match) return@addEventListener
                                deleteBtn.textContent = "..."
                                deleteBtn.style.opacity = "0.6"
                                deleteBtn.style.cursor = "wait"
                                GlobalScope.launch {
                                    try {
                                        DebugLog.log("Calling delete_own_account RPC...")
                                        val res = Supa.rpc("delete_own_account")
                                        DebugLog.log("delete_own_account result: error=${res.error}")
                                        Supa.signOut()
                                    } catch (e: Exception) {
                                        DebugLog.error("delete_own_account exception: ${e.message}")
                                    }
                                    onLogout()
                                }
                            })
                        })
                    }
                })
            }
        }
    }

    private fun reRender(container: HTMLDivElement, userId: String, onLogout: () -> Unit) {
        container.clear()
        render(container, userId, onLogout)
    }

    // ── Profile Card ──
    private fun renderProfileCard(profile: Profile, userId: String): HTMLElement {
        editHeight = if (editHeight.isBlank() || editHeight == "0") profile.height.toString() else editHeight
        editGoalWeight = if (editGoalWeight.isBlank() && profile.goalWeight > 0) fmt1(profile.goalWeight) else editGoalWeight
        editBirthdate = editBirthdate.ifBlank { profile.birthdate }
        editSex = editSex.ifBlank { profile.sex }

        val LBL = Styles.LBL_STYLE
        val INP = Styles.INP_STYLE

        return div {
            style.cssText = Styles.CARD

            // Header
            appendChild(div {
                style.cssText = "display:flex;align-items:center;justify-content:space-between;margin-bottom:20px"
                appendChild(div {
                    style.cssText = "font-size:15px;font-weight:800;text-transform:uppercase;letter-spacing:2px;color:${Styles.TEXT}"
                    textContent = t("settings_profile")
                })
                appendChild(div { id = "profile-save-status"; style.cssText = "font-size:10px;color:${Styles.TEXT_20}" })
            })

            // Height + Goal - simple 2-col row
            appendChild(div {
                style.cssText = "display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:16px"

                // Height
                appendChild(div {
                    appendChild(div { style.cssText = LBL; textContent = t("onboard_height") })
                    appendChild(div {
                        style.cssText = "display:flex;align-items:center;gap:6px"
                        appendChild(input("number") {
                            style.cssText = "$INP;flex:1"
                            value = editHeight
                            placeholder = "170"
                            oninput = { editHeight = this.value; Unit }
                            onchange = {
                                editHeight = this.value
                                DataService.profile = DataService.profile.copy(height = editHeight.toIntOrNull() ?: DataService.profile.height)
                                autoSaveProfile(userId)
                                Unit
                            }
                        })
                        appendChild(span("cm") { style.cssText = "font-size:13px;color:${Styles.TEXT_30};font-weight:600" })
                    })
                })

                // Goal
                appendChild(div {
                    appendChild(div { style.cssText = LBL; textContent = t("goal_title") })
                    appendChild(div {
                        style.cssText = "display:flex;align-items:center;gap:6px"
                        appendChild(input("number") {
                            style.cssText = "$INP;flex:1"
                            value = editGoalWeight
                            placeholder = "75.0"
                            step = "0.1"
                            oninput = { editGoalWeight = this.value; Unit }
                            onchange = {
                                editGoalWeight = this.value
                                val gw = editGoalWeight.toDoubleOrNull() ?: 0.0
                                if (gw == 0.0 || gw in 20.0..300.0) {
                                    DataService.profile = DataService.profile.copy(goalWeight = gw)
                                    autoSaveProfile(userId)
                                }
                                Unit
                            }
                        })
                        appendChild(span("kg") { style.cssText = "font-size:13px;color:${Styles.TEXT_30};font-weight:600" })
                    })
                })
            })

            // Birthdate
            val bdAge = if (editBirthdate.isNotBlank()) calcAge(editBirthdate) else -1
            val ageDisplay = if (bdAge > 0) "$bdAge ${t("years_short")}" else ""
            val bdInvalid = editBirthdate.isNotBlank() && bdAge > 0 && bdAge !in 6..110

            appendChild(div {
                style.cssText = "margin-bottom:16px"
                appendChild(div {
                    style.cssText = "display:flex;align-items:center;justify-content:space-between;margin-bottom:6px"
                    appendChild(div { style.cssText = LBL + ";margin-bottom:0"; textContent = t("label_birthdate") })
                    if (ageDisplay.isNotBlank()) {
                        appendChild(span(ageDisplay) { style.cssText = "font-size:13px;font-weight:800;color:${if (bdInvalid) "#fb7185" else "#818cf8"}" })
                    }
                })
                appendChild(input("date") {
                    style.cssText = "$INP;color-scheme:${Styles.COLOR_SCHEME}"
                    value = editBirthdate
                    oninput = { editBirthdate = this.value; Unit }
                    onchange = {
                        editBirthdate = this.value
                        val age = calcAge(this.value)
                        if (age in 6..110) {
                            DataService.profile = DataService.profile.copy(birthdate = this.value)
                            autoSaveProfile(userId)
                        }
                        MainApp.renderPage()
                        Unit
                    }
                })
                if (bdInvalid) {
                    appendChild(div {
                        style.cssText = "margin-top:6px;font-size:12px;font-weight:600;color:#fb7185"
                        textContent = t("onboard_err_birthdate")
                    })
                }
            })

            // Gender toggle
            appendChild(div {
                style.cssText = "margin-bottom:16px"
                appendChild(div { style.cssText = LBL; textContent = t("label_gender") })
                appendChild(div {
                    style.cssText = "display:flex;gap:8px"
                    listOf("m" to t("gender_male"), "f" to t("gender_female")).forEach { (code, name) ->
                        val isActive = editSex == code
                        val c = if (code == "f") "#f472b6" else Styles.A1
                        appendChild(button(name) {
                            style.cssText = "flex:1;padding:10px;border-radius:10px;border:1.5px solid ${if (isActive) "${c}40" else Styles.CARD_BORDER};background:${if (isActive) "${c}15" else Styles.CARD_BG};color:${if (isActive) c else Styles.TEXT_SEC};font-size:14px;font-weight:700;cursor:pointer;transition:all 0.2s"
                            onclick = {
                                editSex = code
                                DataService.profile = DataService.profile.copy(sex = code)
                                autoSaveProfile(userId)
                                MainApp.renderPage()
                                Unit
                            }
                        })
                    }
                })
            })

            // BMI removed - shown on Weight tab
        }
    }

    private fun autoSaveProfile(userId: String) {
        val h = editHeight.toIntOrNull() ?: DataService.profile.height
        val gw = editGoalWeight.toDoubleOrNull() ?: 0.0
        val bd = editBirthdate
        val sx = editSex.ifBlank { DataService.profile.sex }

        DebugLog.log("autoSaveProfile called: h=$h, gw=$gw, bd='$bd', sex='$sx', userId=$userId")

        GlobalScope.launch {
            try {
                val updatedProfile = DataService.profile.copy(
                    height = h,
                    goalWeight = gw,
                    birthdate = bd,
                    sex = sx
                )
                DebugLog.log("Calling DataService.saveProfile...")
                val ok = DataService.saveProfile(userId, updatedProfile)
                DebugLog.log("saveProfile returned: $ok")

                if (ok) {
                    DataService.profile = updatedProfile
                    DebugLog.ok("Profile updated in memory + DB")

                    // Verify by re-loading
                    DataService.loadProfile(userId)
                    DebugLog.log("Re-loaded profile: h=${DataService.profile.height}, gw=${DataService.profile.goalWeight}, bd=${DataService.profile.birthdate}, sex=${DataService.profile.sex}")

                    kotlinx.browser.document.getElementById("profile-save-status")?.let {
                        it.textContent = "\u2705 Ulozene"
                        (it as HTMLElement).style.color = Styles.A1
                        kotlinx.browser.window.setTimeout({
                            it.textContent = ""
                        }, 2500)
                    }
                } else {
                    DebugLog.error("Profile save returned FALSE")
                    kotlinx.browser.document.getElementById("profile-save-status")?.let {
                        it.textContent = "\u274C Chyba"
                        (it as HTMLElement).style.color = "#fb7185"
                    }
                }
            } catch (e: Exception) {
                DebugLog.error("autoSaveProfile EXCEPTION: ${e.message}")
                DebugLog.error("Stack: ${e.stackTraceToString().take(300)}")
                kotlinx.browser.document.getElementById("profile-save-status")?.let {
                    it.textContent = "\u274C ${e.message}"
                    (it as HTMLElement).style.color = "#fb7185"
                }
            }
        }
    }

    // ── Theme Selector ──
    private fun renderThemeSelector(): HTMLElement {
        return div {
            style.cssText = Styles.CARD

            appendChild(div {
                style.cssText = "font-size:15px;color:${Styles.TEXT};font-weight:800;text-transform:uppercase;letter-spacing:2px;margin-bottom:12px"
                textContent = t("theme_toggle")
            })

            appendChild(div {
                style.cssText = "display:flex;gap:6px"
                data class ThemeOpt(val key: String, val label: String, val icon: String)
                listOf(
                    ThemeOpt("dark", t("theme_dark"), "\uD83C\uDF19"),
                    ThemeOpt("light", t("theme_light"), "\u2600\uFE0F")
                ).forEach { opt ->
                    val isActive = (opt.key == "dark" && ThemeManager.isDark()) || (opt.key == "light" && !ThemeManager.isDark())
                    appendChild(button("${opt.icon} ${opt.label}") {
                        style.cssText = "flex:1;padding:10px 8px;border-radius:12px;border:${if (isActive) "1.5px solid ${Styles.A1}50" else "1.5px solid ${Styles.CARD_BORDER}"};background:${if (isActive) "${Styles.A1}15" else Styles.CARD_BG};color:${if (isActive) Styles.TEXT else Styles.TEXT_SEC};font-size:13px;font-weight:700;cursor:pointer"
                        onclick = {
                            if (!isActive) {
                                ThemeManager.toggle()
                                MainApp.navigateTo("sett")
                            }
                            Unit
                        }
                    })
                }
            })
        }
    }

    // ── Timezone Info ──
    private fun getTimezone(): String {
        return try {
            val tz = js("Intl.DateTimeFormat().resolvedOptions().timeZone")
            tz?.toString() ?: "Unknown"
        } catch (_: Exception) { "Unknown" }
    }

    private fun getUtcOffset(): String {
        val offset = kotlin.js.Date().getTimezoneOffset().toInt()
        val h = kotlin.math.abs(offset) / 60
        val m = kotlin.math.abs(offset) % 60
        val sign = if (offset <= 0) "+" else "-"
        return "UTC${sign}${h}${if (m > 0) ":${m.toString().padStart(2, '0')}" else ""}"
    }

    private fun getSavedTimezone(): String {
        val saved = try { kotlinx.browser.window.localStorage.getItem("wat_timezone") } catch (_: Exception) { null }
        return saved ?: getTimezone()
    }

    private fun getAllTimezones(): List<String> {
        return try {
            val arr = js("Intl.supportedValuesOf('timeZone')") as Array<String>
            arr.toList()
        } catch (_: Exception) {
            // Fallback for older browsers
            listOf(
                "Europe/Bratislava", "Europe/Prague", "Europe/Berlin", "Europe/Vienna",
                "Europe/London", "Europe/Paris", "Europe/Rome", "Europe/Madrid",
                "Europe/Warsaw", "Europe/Budapest", "Europe/Amsterdam", "Europe/Moscow",
                "America/New_York", "America/Chicago", "America/Los_Angeles",
                "Asia/Tokyo", "Asia/Shanghai", "Asia/Dubai", "Asia/Kolkata",
                "Australia/Sydney", "Pacific/Auckland"
            )
        }
    }

    private fun renderTimezoneInfo(): HTMLElement {
        val currentTz = getSavedTimezone()
        val offset = getUtcOffset()

        return div {
            style.cssText = "margin-bottom:16px"

            appendChild(div {
                style.cssText = "font-size:15px;font-weight:800;text-transform:uppercase;letter-spacing:2px;color:${Styles.TEXT};margin-bottom:10px"
                textContent = t("settings_timezone")
            })

            appendChild(div {
                style.cssText = "display:flex;align-items:center;gap:8px"

                appendChild(select {
                    style.cssText = "width:100%;padding:10px 12px;border-radius:10px;border:1.5px solid ${Styles.CARD_BORDER};background:${Styles.CARD_BG};color:${Styles.TEXT};font-size:14px;font-weight:600;font-family:inherit;outline:none;cursor:pointer"
                    // Short curated list
                    val zones = listOf(
                        "Europe/Bratislava", "Europe/Prague", "Europe/Warsaw", "Europe/Berlin",
                        "Europe/Vienna", "Europe/Budapest", "Europe/London", "Europe/Paris",
                        "Europe/Rome", "Europe/Madrid", "Europe/Amsterdam", "Europe/Moscow",
                        "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
                        "Asia/Tokyo", "Asia/Shanghai", "Asia/Dubai", "Asia/Kolkata",
                        "Australia/Sydney", "Pacific/Auckland"
                    )
                    zones.forEach { tz ->
                        appendChild(option(tz.replace("_", " "), tz) {
                            if (tz == currentTz) selected = true
                        })
                    }
                    onchange = { e ->
                        val tz = (e.target as HTMLSelectElement).value
                        try { kotlinx.browser.window.localStorage.setItem("wat_timezone", tz) } catch (_: Exception) {}
                        MainApp.renderPage()
                        Unit
                    }
                })
            })
        }
    }

    // ── Language Selector ──
    private val langFlagCodes = mapOf("sk" to "sk", "en" to "gb", "cz" to "cz", "de" to "de")

    private fun renderLanguageSelector(profile: Profile, userId: String): HTMLElement {
        val fc = langFlagCodes[I18n.currentLang] ?: "sk"
        val currentName = I18n.langNames[I18n.currentLang] ?: "Slovenčina"

        return div {
            style.cssText = Styles.CARD

            // Header row - flag + name hide when dropdown open
            val headerRight = div {
                style.cssText = "display:flex;align-items:center;gap:8px"
                innerHTML = "<img src='https://flagcdn.com/28x21/${fc}.png' width='22' height='16' style='border-radius:3px' alt=''/>"
                appendChild(span(currentName) {
                    style.cssText = "font-size:14px;font-weight:600;color:${Styles.TEXT_60}"
                })
                appendChild(div {
                    innerHTML = "<svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='${Styles.TEXT_TER}' stroke-width='2.5' stroke-linecap='round'><path d='M6 9l6 6 6-6'/></svg>"
                })
            }

            val dropdownDiv = div { style.cssText = "display:none;padding-top:12px" }

            val headerRow = div {
                style.cssText = "display:flex;align-items:center;gap:12px;cursor:pointer"
                appendChild(div {
                    style.cssText = "font-size:15px;color:${Styles.TEXT};font-weight:800;text-transform:uppercase;letter-spacing:2px"
                    textContent = t("label_language")
                })
                appendChild(div { style.cssText = "flex:1" })
                appendChild(headerRight)
            }
            headerRow.onclick = {
                val opening = dropdownDiv.style.display == "none"
                dropdownDiv.style.display = if (opening) "block" else "none"
                headerRight.style.display = if (opening) "none" else "flex"
                Unit
            }
            appendChild(headerRow)

            // Dropdown list
            I18n.langNames.forEach { (code, name) ->
                val isActive = I18n.currentLang == code
                val flagC = langFlagCodes[code] ?: code
                dropdownDiv.appendChild(div {
                    style.cssText = "display:flex;align-items:center;gap:12px;padding:12px 4px;cursor:pointer;border-radius:10px;${if (isActive) "background:${Styles.A1}10" else ""}"
                    innerHTML = "<img src='https://flagcdn.com/32x24/${flagC}.png' width='28' height='20' style='border-radius:4px;flex-shrink:0;${if (isActive) "box-shadow:0 0 0 2px ${Styles.A1}" else "opacity:0.6"}' alt=''/>"
                    appendChild(span(name) {
                        style.cssText = "flex:1;font-size:15px;font-weight:${if (isActive) "700" else "500"};color:${if (isActive) "${Styles.TEXT}" else "${Styles.TEXT_40}"}"
                    })
                    if (isActive) {
                        appendChild(span("\u2713") {
                            style.cssText = "font-size:18px;font-weight:900;color:${Styles.A1};text-shadow:0 0 8px ${Styles.A1}"
                        })
                    }
                    onclick = {
                        I18n.currentLang = code
                        GlobalScope.launch {
                            val updatedProfile = profile.copy(lang = code)
                            DataService.saveProfile(userId, updatedProfile)
                            DataService.profile = updatedProfile
                        }
                        MainApp.renderPage()
                        Unit
                    }
                })
            }
            appendChild(dropdownDiv)
        }
    }

    // ── Default Page Selector ──
    private fun renderDefaultPageSelector(profile: Profile, userId: String): HTMLElement {
        data class PageOption(val key: String, val label: String, val icon: String)
        val pages = listOf(
            PageOption("train", t("nav_train"), "\u26A1"),
            PageOption("home", t("nav_home"), "\u2696\uFE0F"),
            PageOption("diary", t("nav_diary"), "\uD83D\uDCD6")
        )

        return div {
            style.cssText = Styles.CARD

            appendChild(div {
                style.cssText = "font-size:15px;color:${Styles.TEXT};font-weight:800;text-transform:uppercase;letter-spacing:2px;margin-bottom:12px"
                textContent = t("settings_default_page")
            })

            appendChild(div {
                style.cssText = "display:flex;gap:6px"
                pages.forEach { pg ->
                    val isActive = profile.defaultPage == pg.key
                    appendChild(button("${pg.icon} ${pg.label}") {
                        style.cssText = "flex:1;padding:10px 8px;border-radius:12px;border:${if (isActive) "1.5px solid ${Styles.A1}50" else "1.5px solid ${Styles.CARD_BORDER}"};background:${if (isActive) "${Styles.A1}15" else "${Styles.CARD_BG}"};color:${if (isActive) "${Styles.TEXT}" else "${Styles.TEXT_SEC}"};font-size:13px;font-weight:700;cursor:pointer"
                        onclick = {
                            GlobalScope.launch {
                                val updatedProfile = profile.copy(defaultPage = pg.key)
                                DataService.saveProfile(userId, updatedProfile)
                                DataService.profile = updatedProfile
                                MainApp.renderPage()
                            }
                            Unit
                        }
                    })
                }
            })
        }
    }

    // ── Activity Management ──
    private var activitySearch = ""

    private fun renderActivityManagement(): HTMLElement {
        val catOrder = DataService.categories.map { it.key }

        val listContainer = div { id = "activity-list" }

        fun rebuildList() {
            listContainer.clear()
            val query = activitySearch.lowercase()
            val filteredSports = if (query.isBlank()) DataService.sports else DataService.sports.filter {
                it.localName().lowercase().contains(query) || it.l.lowercase().contains(query) || it.i.contains(query) || it.k.contains(query)
            }
            val sportsByCat = filteredSports.groupBy { it.cat }

            fun renderCategoryBlock(catKey: String, catSports: List<Sport>) {
                if (catSports.isEmpty()) return
                val catLabel = t("cat_$catKey").let { if (it == "cat_$catKey") catKey.replaceFirstChar { c -> c.uppercase() } else it }
                val enabledCount = catSports.count { enabledSports[it.k] ?: it.isDefault }

                // Category header - bold, visible
                listContainer.appendChild(div {
                    style.cssText = "display:flex;align-items:center;gap:12px;padding:28px 0 12px"
                    appendChild(span(catLabel) {
                        style.cssText = "font-size:16px;font-weight:900;text-transform:uppercase;letter-spacing:3px;color:${Styles.TEXT}"
                    })
                    appendChild(div { style.cssText = "flex:1;height:2px;background:${Styles.TEXT_15}" })
                    appendChild(span("$enabledCount/${catSports.size}") {
                        style.cssText = "font-size:14px;font-weight:800;color:${Styles.A1}"
                    })
                })

                // Sport rows card
                listContainer.appendChild(div {
                    style.cssText = "background:${Styles.CARD_BG};border:1.5px solid ${Styles.CARD_BORDER};border-radius:14px;margin-bottom:4px;overflow:hidden"

                    // Sport rows - sorted by color hue for visual grouping
                    appendChild(div {
                        style.cssText = "padding:4px 0"

                        catSports.forEach { sp ->
                            val c = Styles.adaptColor(sp.color.ifEmpty { Styles.A1 })
                            val isOn = enabledSports[sp.k] ?: sp.isDefault

                            appendChild(div {
                                style.cssText = "display:flex;align-items:center;gap:0;padding:2px 0;cursor:pointer"
                                onclick = {
                                    enabledSports[sp.k] = !isOn
                                    val def = DataService.sportDefs.getOrPut(sp.k) { SportDef(on = sp.isDefault) }
                                    DataService.sportDefs[sp.k] = def.copy(on = !isOn)
                                    DataService.saveSportDefs()
                                    rebuildList()
                                    Unit
                                }

                                // Color bar on left edge
                                appendChild(div {
                                    style.cssText = "width:3px;height:32px;border-radius:2px;background:${if (isOn) c else Styles.TEXT_10};flex-shrink:0;margin-right:10px;transition:background 0.2s"
                                })

                                // Icon
                                appendChild(span(sp.i) {
                                    style.cssText = "font-size:20px;width:28px;text-align:center;flex-shrink:0;opacity:${if (isOn) "1" else "0.25"};transition:opacity 0.2s"
                                })

                                // Name - colored when active
                                appendChild(span(sp.localName()) {
                                    style.cssText = "flex:1;font-size:15px;font-weight:${if (isOn) "600" else "500"};color:${if (isOn) c else Styles.TEXT_TER};margin-left:8px;transition:color 0.2s"
                                })

                                // Unit
                                appendChild(span(I18n.tU(sp.u)) {
                                    style.cssText = "font-size:11px;font-weight:600;color:${if (isOn) Styles.TEXT_TER else Styles.TEXT_15};letter-spacing:0.5px;min-width:28px;text-align:right"
                                })

                                // Toggle
                                appendChild(div {
                                    style.cssText = "width:22px;height:22px;border-radius:11px;flex-shrink:0;margin-left:8px;border:2px solid ${if (isOn) "${c}60" else "${Styles.TEXT_10}"};background:${if (isOn) c else "transparent"};transition:all 0.2s;display:flex;align-items:center;justify-content:center"
                                    if (isOn) {
                                        appendChild(span("\u2713") {
                                            style.cssText = "font-size:12px;color:#fff;font-weight:900;line-height:1"
                                        })
                                    }
                                })
                            })
                        }
                    })
                })
            }

            catOrder.forEach { catKey ->
                val catSports = sportsByCat[catKey] ?: return@forEach
                renderCategoryBlock(catKey, catSports)
            }

            val knownCats = catOrder.toSet()
            val unknownSports = filteredSports.filter { it.cat !in knownCats }
            if (unknownSports.isNotEmpty()) {
                renderCategoryBlock("ostatne", unknownSports)
            }
        }

        return div {
            // Search bar - stays in DOM, never re-rendered
            appendChild(input("text") {
                style.cssText = "width:100%;padding:10px 14px;border-radius:10px;border:1.5px solid ${Styles.CARD_BORDER};background:${Styles.CARD_BG};color:${Styles.TEXT};font-size:13px;font-weight:500;outline:none;box-sizing:border-box;font-family:inherit;margin-bottom:8px"
                placeholder = t("diary_search_placeholder")
                value = activitySearch
                oninput = { e ->
                    activitySearch = (e.target as HTMLInputElement).value
                    rebuildList()
                    Unit
                }
            })

            // Bulk actions: all on / all off / defaults only
            appendChild(div {
                style.cssText = "display:flex;gap:6px;margin-bottom:12px"

                // All on
                appendChild(button(t("cat_all") + " \u2713") {
                    style.cssText = "flex:1;padding:8px 0;border-radius:8px;border:1.5px solid ${Styles.A1}40;background:${Styles.CARD_BG};color:${Styles.A1};font-size:11px;font-weight:700;cursor:pointer;font-family:inherit"
                    onclick = {
                        DataService.sports.forEach { sp ->
                            enabledSports[sp.k] = true
                            val def = DataService.sportDefs.getOrPut(sp.k) { SportDef(on = true) }
                            DataService.sportDefs[sp.k] = def.copy(on = true)
                        }
                        DataService.saveSportDefs()
                        rebuildList()
                        Unit
                    }
                })

                // All off
                appendChild(button(t("cat_all") + " \u2715") {
                    style.cssText = "flex:1;padding:8px 0;border-radius:8px;border:1.5px solid #fb718540;background:${Styles.CARD_BG};color:#fb7185;font-size:11px;font-weight:700;cursor:pointer;font-family:inherit"
                    onclick = {
                        DataService.sports.forEach { sp ->
                            enabledSports[sp.k] = false
                            val def = DataService.sportDefs.getOrPut(sp.k) { SportDef(on = false) }
                            DataService.sportDefs[sp.k] = def.copy(on = false)
                        }
                        DataService.saveSportDefs()
                        rebuildList()
                        Unit
                    }
                })

                // Defaults only
                appendChild(button(t("settings_default")) {
                    style.cssText = "flex:1;padding:8px 0;border-radius:8px;border:1.5px solid ${Styles.CARD_BORDER};background:${Styles.CARD_BG};color:${Styles.TEXT_SEC};font-size:11px;font-weight:700;cursor:pointer;font-family:inherit"
                    onclick = {
                        DataService.sports.forEach { sp ->
                            enabledSports[sp.k] = sp.isDefault
                            val def = DataService.sportDefs.getOrPut(sp.k) { SportDef(on = sp.isDefault) }
                            DataService.sportDefs[sp.k] = def.copy(on = sp.isDefault)
                        }
                        DataService.saveSportDefs()
                        rebuildList()
                        Unit
                    }
                })
            })

            rebuildList()
            appendChild(listContainer)
        }
    }

    // ── DB Status Panel ──
    private fun renderDbStatus(): HTMLElement {
        data class DbStat(val label: String, val value: String, val color: String)

        return div {
            style.cssText = Styles.CARD

            appendChild(div {
                style.cssText = "font-size:15px;color:${Styles.TEXT};font-weight:800;text-transform:uppercase;letter-spacing:2px;margin-bottom:16px"
                textContent = t("settings_db_status")
            })

            listOf(
                DbStat(t("db_activities"), "${DataService.sports.size}", Styles.adaptColor(Styles.A1)),
                DbStat(t("db_entries"), "${DataService.entries.size}", Styles.adaptColor("#818cf8")),
                DbStat(t("db_categories"), "${DataService.categories.size}", Styles.adaptColor("#c084fc")),
                DbStat(t("db_diary"), "${DataService.diaryEntries.size}", Styles.adaptColor("#38bdf8")),
                DbStat(t("db_units"), "${DataService.units.size}", Styles.YELLOW),
                DbStat(t("db_bmi_zones"), "${DataService.bmiZones.size}", Styles.adaptColor("#f472b6"))
            ).forEach { stat ->
                appendChild(div {
                    style.cssText = "display:flex;align-items:center;justify-content:space-between;padding:8px 0;border-bottom:1px solid ${Styles.SEPARATOR}"
                    appendChild(span(stat.label) {
                        style.cssText = "font-size:13px;color:${Styles.TEXT_SEC};font-weight:600"
                    })
                    appendChild(div {
                        style.cssText = "display:flex;align-items:center;gap:6px"
                        appendChild(span(stat.value) {
                            style.cssText = "font-size:15px;font-weight:800;color:${stat.color}"
                        })
                        appendChild(div {
                            style.cssText = "width:6px;height:6px;border-radius:3px;background:#2dd4bf;${if (ThemeManager.isDark()) "box-shadow:0 0 6px #2dd4bf" else ""}"
                        })
                    })
                })
            }
        }
    }
}
